import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CouponService } from '@rma/billing/public/api-coupons';
import { CurrentSubscriptionService, SubscriptionProductService } from '@rma/billing/subscription-plans/public/api-subscription-features';
import { SubscriptionTiers } from '@rma/generic/domain/to-be-sorted';
import { rmaShareReplay } from '@rma/generic/util/operators/rma-share-replay';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AwardWinnerBannerComponent } from '../ui-award-winner/award-winner-banner.component';
import { BlackFridayBannerComponent } from '../ui-black-friday-banner/black-friday-banner.component';
import { CyberMondayBannerComponent } from '../ui-cyber-monday-banner/cyber-monday-banner.component';
import { DefaultCouponBannerComponent } from '../ui-default-coupon-banner/default-coupon-banner.component';
import { EofyBannerComponent } from '../ui-eofy-banner/eofy-banner.component';
import { MarchMadnessBannerComponent } from '../ui-march-madness-banner/march-madness-banner.component';
import { RemaxBannerComponent } from '../ui-remax-banner/remax-banner.component';
import { SummerSaleBannerComponent } from '../ui-summer-sale-banner/summer-sale-banner.component';
import { PromotionBannerStyle } from './promotion-banner-style.enum';
import { PromotionType } from './promotion-type.enum';
import { PromotionService } from './promotion.service';

@UntilDestroy()
@Component({
  selector: 'rma-promotion-banner',
  templateUrl: './promotion-banner.component.html',
  styleUrls: ['./promotion-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    AwardWinnerBannerComponent,
    BlackFridayBannerComponent,
    CyberMondayBannerComponent,
    DefaultCouponBannerComponent,
    EofyBannerComponent,
    MarchMadnessBannerComponent,
    RemaxBannerComponent,
    SummerSaleBannerComponent,
  ],
})
export class PromotionBannerComponent {
  @HostBinding()
  public class = '';

  @Input()
  public bannerStyle: PromotionBannerStyle = 'Full';

  public hasAvailableTrialPeriod$: Observable<boolean>;
  public promotionType$: Observable<PromotionType>;

  public constructor(
    private readonly couponService: CouponService,
    private readonly currentSubscriptionService: CurrentSubscriptionService,
    private readonly promotionService: PromotionService,
    private readonly subscriptionProductService: SubscriptionProductService,
  ) {
    this.hasAvailableTrialPeriod$ = this.subscriptionProductService.availableTrialDays$.pipe(map((days) => days > 0));

    this.promotionType$ = combineLatest([
      this.promotionService.promotionType$,
      this.couponService.coupon(),
      this.currentSubscriptionService.currentSubscription$,
    ]).pipe(
      map(([promotion, coupon, subscription]): PromotionType => {
        if (promotion !== 'None') {
          return promotion;
        }

        if (coupon !== null && (subscription.isFree || subscription.tier === SubscriptionTiers.Starter)) {
          return 'DefaultCoupon';
        }

        return 'None';
      }),
      rmaShareReplay(),
    );

    this.promotionType$.pipe(untilDestroyed(this)).subscribe((type) => (this.class = type === 'None' ? 'hidden' : ''));
  }
}
